// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Reusable panel that lists prior conflict checks for a given client.
// Used in two places:
//   1. The standalone Conflict Check tool, when a client has been selected
//   2. The "Conflicts" tab on the client detail page
//
// Same component, same disclaimer, same render — keeps the audit-trail UI
// consistent across both surfaces.

import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/adminApi';

const td = { padding: '8px 12px', fontSize: 13, fontFamily: 'Arial, sans-serif', borderBottom: '1px solid #eee', verticalAlign: 'top' };
const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838' };

/**
 * Bright yellow/amber disclaimer banner. Used at the top of the conflict
 * tool, above the prior-checks list, and inside any saved check view.
 * Wording is deliberate and should not be softened — it's the legal CYA.
 */
export function ConflictCheckDisclaimer({ compact = false }) {
  return (
    <div
      style={{
        background: '#fff8e1',
        border: '1px solid #f0d68a',
        borderLeft: '4px solid #b7870a',
        borderRadius: 4,
        padding: compact ? '10px 14px' : '14px 18px',
        marginBottom: 16,
        fontFamily: 'Arial, sans-serif',
        fontSize: compact ? 12 : 13,
        lineHeight: 1.5,
        color: '#5a4500',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        ⚠ Limitation of this check
      </div>
      <div>
        This tool searches only the structured fields (name, email, phone,
        company) of <strong>clients</strong> and <strong>contacts</strong>{' '}
        that have been entered into this portal. It does <strong>not</strong>{' '}
        search physical files, email inboxes, cloud document storage
        (Dropbox, iCloud, OneDrive, Google Drive), text messages, paper
        intake forms, prior firm matters from before this portal was set up,
        or any matter where the involved party was not entered as a
        structured field. A &quot;no match&quot; result is{' '}
        <strong>not</strong> a determination that no conflict exists. The
        attorney remains responsible for completing a full conflict check.
      </div>
    </div>
  );
}

export default function ConflictChecksPanel({ clientId, refreshKey, onRunNew }) {
  const [checks, setChecks]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [openCheck, setOpenCheck] = useState(null); // expanded check details

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    adminApi.listConflictChecksForClient(clientId)
      .then(data => { if (!cancelled) { setChecks(data); setLoading(false); } })
      .catch(err  => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [clientId, refreshKey]);

  if (loading) {
    return <div style={{ padding: 12, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading prior checks...</div>;
  }
  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div>
      <ConflictCheckDisclaimer compact />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontFamily: 'Arial, sans-serif', fontSize: 16, margin: 0 }}>
          Conflict Check History
        </h3>
        {onRunNew && (
          <button className="btn btn-primary btn-sm" onClick={onRunNew}>
            + Run New Check
          </button>
        )}
      </div>

      {!checks || !checks.length ? (
        <div style={{ padding: '20px 12px', textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13, border: '1px dashed #ddd', borderRadius: 4 }}>
          No conflict checks have been run for this client yet.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Run by</th>
                <th style={th}>Parties searched</th>
                <th style={th}>Matches</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {checks.map((c) => {
                const isOpen = openCheck === c.id;
                const summary = c.summary || {};
                const byConf  = summary.by_confidence || {};
                const total = (byConf.exact || 0) + (byConf.strong || 0) + (byConf.possible || 0);
                const parties = c.parties_searched || [];
                return (
                  <React.Fragment key={c.id}>
                    <tr style={{ background: isOpen ? '#fafafa' : 'white' }}>
                      <td style={td}>{new Date(c.run_at).toLocaleString()}</td>
                      <td style={td}>{c.run_by_name || <span style={{ color: '#888' }}>(unknown)</span>}</td>
                      <td style={td}>
                        {parties.length} part{parties.length === 1 ? 'y' : 'ies'}
                        <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                          {parties.slice(0, 2).map(p => p.name).join(', ')}
                          {parties.length > 2 ? `, +${parties.length - 2} more` : ''}
                        </div>
                      </td>
                      <td style={td}>
                        {total === 0 ? (
                          <span style={{ color: '#1a7a5e', fontWeight: 600 }}>None</span>
                        ) : (
                          <span>
                            <strong>{total}</strong>{' '}
                            <span style={{ fontSize: 11, color: '#666' }}>
                              ({byConf.exact || 0} exact, {byConf.strong || 0} strong, {byConf.possible || 0} possible)
                            </span>
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button className="btn btn-sm" onClick={() => setOpenCheck(isOpen ? null : c.id)}>
                          {isOpen ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5} style={{ ...td, background: '#fafafa' }}>
                          <SavedCheckDetail checkId={c.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Expanded view of one saved check. Calls /api/conflict-check/:id which
// RE-RUNS the search against current data, so the displayed matches reflect
// the current state of clients/contacts (not a frozen snapshot).
function SavedCheckDetail({ checkId }) {
  const [detail, setDetail] = useState(null);
  const [error, setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    adminApi.getConflictCheck(checkId)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [checkId]);

  if (error)  return <div className="alert alert-error">{error}</div>;
  if (!detail) return <div style={{ color: '#888' }}>Loading...</div>;

  const matches = detail.current_matches || [];
  const parties = detail.parties_searched || [];

  return (
    <div style={{ fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
      <div style={{ fontStyle: 'italic', color: '#888', fontSize: 11, marginBottom: 10 }}>
        Results below reflect the <strong>current</strong> state of clients and contacts —
        they may differ from the count above if data has changed since the check was run.
      </div>

      <div style={{ marginBottom: 12 }}>
        <strong>Parties searched:</strong>
        <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
          {parties.map((p, i) => (
            <li key={i} style={{ marginBottom: 2 }}>
              <strong>{p.name}</strong>
              {p.relation && <span style={{ color: '#666' }}> — {p.relation}</span>}
              {(p.email || p.phone) && (
                <span style={{ color: '#888', fontSize: 11 }}>
                  {' '}({[p.email, p.phone].filter(Boolean).join(' • ')})
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <MatchesList matches={matches} />
    </div>
  );
}

export function MatchesList({ matches }) {
  if (!matches || !matches.length) {
    return (
      <div style={{
        background: '#e8f7ef',
        border: '1px solid #b8e3cf',
        color: '#1a7a5e',
        padding: '10px 14px',
        borderRadius: 4,
        fontFamily: 'Arial, sans-serif',
        fontSize: 13,
        fontWeight: 600,
      }}>
        ✓ No matches found in clients or contacts. (Remember: this does not mean no conflict exists.)
      </div>
    );
  }

  // Group by confidence so the most important results appear first.
  const order = ['exact', 'strong', 'possible'];
  const grouped = { exact: [], strong: [], possible: [] };
  for (const m of matches) grouped[m.confidence || 'possible'].push(m);

  return (
    <div>
      {order.map(level => grouped[level].length > 0 && (
        <div key={level} style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            marginBottom: 6,
            color: level === 'exact' ? '#B85042' : level === 'strong' ? '#b7870a' : '#666',
          }}>
            {level} matches ({grouped[level].length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #eee' }}>
            <thead>
              <tr>
                <th style={th}>Source</th>
                <th style={th}>Name</th>
                <th style={th}>Searched for</th>
                <th style={th}>Why it matched</th>
              </tr>
            </thead>
            <tbody>
              {grouped[level].map((m, i) => (
                <tr key={i}>
                  <td style={td}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      background: m.source === 'client' ? '#e8f0fb' : '#f0eef8',
                      color: m.source === 'client' ? '#2f5496' : '#5e4b9e',
                      fontWeight: 600,
                    }}>
                      {m.source === 'client' ? 'Client' : 'Contact'}
                    </span>
                  </td>
                  <td style={td}>
                    <strong>{m.display_name}</strong>
                    {m.company && <div style={{ fontSize: 11, color: '#666' }}>{m.company}</div>}
                    {m.source === 'client' && (
                      <a href={`/admin/clients/${m.id}`} target="_blank" rel="noreferrer"
                         style={{ fontSize: 11, color: '#2f5496', textDecoration: 'underline' }}>
                        Open client →
                      </a>
                    )}
                  </td>
                  <td style={td}>
                    {m.party_name}
                    {m.party_relation && (
                      <div style={{ fontSize: 11, color: '#666' }}>{m.party_relation}</div>
                    )}
                  </td>
                  <td style={td}>
                    <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                      {(m.reasons || []).map((r, j) => (
                        <li key={j} style={{ fontSize: 12 }}>{r.detail}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
